import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import AppLayout from "../../layouts/app/AppLayout";
import MinimalLayout from "../../layouts/minimal/MinimalLayout";
import PublicLayout from "../../layouts/public/PublicLayout";
import LoginPage from "../../features/auth/LoginPage";
import RootRedirect from "./RootRedirect";
import ProtectedRoute from "./guards/ProtectedRoute";
import EmailVerifiedRoute from "./guards/EmailVerifiedRoute";
import RouteFallback from "./fallback/RouteFallback";

const ChannelPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/channels/overview/ChannelOverviewPage"));
const ChannelPageOptions = lazy(() => import("../../features/channels/options"));
const ChannelIntegrationQrCodePage = lazy(() => import("../../features/channels/types/WhatsappWeb/Integration/QRCode/ChannelIntegrationQrCodePage"));
const CreateIntegrationAlcancePage = lazy(() => import("../../features/channels/types/Sms/Integration/Alcance/CreateIntegrationAlcancePage"));
const CreateIntegrationSesPage = lazy(() => import("../../features/channels/types/Email/Integration/Ses/CreateIntegrationSesPage"));
const CreateIntegrationEmbeddedPage = lazy(() => import("../../features/channels/types/WhatsappApi/Integration/EmbeddedSignup/CreateIntegrationEmbeddedPage"));
const ViewConfigChannel = lazy(() => import("../../features/channels/settings"));

const TemplatesPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/templates/overview/TemplatesOverviewPage"));
const TemplatePageAdd = lazy(() => import("../../features/templates/options"));
const TemplatePageChannelAdd = lazy(() => import("../../features/templates/create"));

const IntegrationsPageAdd = lazy(() => import(/* webpackPrefetch: true */ "../../features/integrations/options"));
const AddIntegration = lazy(() => import("../../features/integrations/AddIntegration"));

const PasswordRecoveryPage = lazy(() => import("../../features/auth/PasswordRecoveryPage"));
const DashboardPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/dashboard"));

const CampaignCreatePage = lazy(() => import("../../features/campaigns/create/CampaignCreatePage"));
const CampaignEditPage = lazy(() => import("../../features/campaigns/edit/CampaignEditPage"));
const CampaignPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/campaigns/overview"));
const CampaignViewPage = lazy(() => import("../../features/campaigns/view/CampaignViewPage"));

const Sheets = lazy(() => import(/* webpackPrefetch: true */ "../../features/sheets/overview"));
const AddSheetsOptions = lazy(() => import("../../features/sheets/options"));
const PageAddGoogleSheets = lazy(() => import("../../features/sheets/types/GoogleSheets/create/addGoogleSheets"));
const PageAddExcel = lazy(() => import("../../features/sheets/types/Excel/create/addExcel"));
const PageAddApiRest = lazy(() => import("../../features/sheets/types/ApiRest/create/addApiRest"));
const AddSheetsIntegrationInvoice = lazy(() => import("../../features/sheets/types/Erps/create/addSheetsIntegrationInvoice"));

const ProfileSetupPage = lazy(() => import("../../features/profile/ProfileSetupPage"));
const SignUp = lazy(() => import("../../features/auth/SignupPage"));
const VerifyEmailPage = lazy(() => import("../../features/auth/VerifyEmailPage"));

const NotFound = lazy(() => import("../../shared/components/common/ui/NotFound"));
const SessionExpired = lazy(() => import("../../shared/components/common/ui/SessionExpired"));

const SupportAccessPage = lazy(() => import("../../features/auth/SupportAccessPage"));
const ResetRequiredPasswordPage = lazy(() => import("../../features/auth/ResetRequiredPasswordPage"));

export default function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/esqueci-senha" element={<PasswordRecoveryPage />} />
          <Route path="/cadastro" element={<SignUp />} />
          <Route path="/support-login" element={<SupportAccessPage />} />
          <Route path="/reset-senha" element={<ResetRequiredPasswordPage />} />
          <Route path="/logout" element={<SessionExpired />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<MinimalLayout />}>
            <Route path="/verificar-email" element={<VerifyEmailPage />} />
          </Route>

          <Route element={<EmailVerifiedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/painel" element={<DashboardPage />} />
              <Route path="/perfil" element={<ProfileSetupPage />} />

              <Route path="/canais" element={<ChannelPageList />} />
              <Route path="/canais/adicionar" element={<ChannelPageOptions />} />
              <Route path="/canais/adicionar/whatsapp-web" element={<ChannelIntegrationQrCodePage />} />
              <Route path="/canais/adicionar/whatsapp-api" element={<CreateIntegrationEmbeddedPage />} />
              <Route path="/canais/adicionar/sms" element={<CreateIntegrationAlcancePage />} />
              <Route path="/canais/adicionar/email" element={<CreateIntegrationSesPage />} />
              <Route path="/canais/configurar" element={<ViewConfigChannel />} />

              <Route path="/modelos" element={<TemplatesPageList />} />
              <Route path="/modelos/canal" element={<TemplatePageAdd />} />
              <Route path="/modelos/canal/whatsapp-web" element={<TemplatePageChannelAdd channel="whatsapp_web" />} />
              <Route path="/modelos/canal/whatsapp-api" element={<TemplatePageChannelAdd channel="whatsapp_api" />} />
              <Route path="/modelos/canal/email" element={<TemplatePageChannelAdd channel="email" />} />
              <Route path="/modelos/canal/sms" element={<TemplatePageChannelAdd channel="sms" />} />

              <Route path="/listagens" element={<Sheets />} />
              <Route path="/listagens/adicionar" element={<AddSheetsOptions />} />
              <Route path="/listagens/adicionar/google" element={<PageAddGoogleSheets />} />
              <Route path="/listagens/adicionar/api" element={<PageAddApiRest />} />
              <Route path="/listagens/adicionar/excel" element={<PageAddExcel />} />
              <Route path="/listagens/adicionar/:integration" element={<AddSheetsIntegrationInvoice />} />
              <Route path="/listagens/adicionar/:integration/faturas em aberto" element={<AddSheetsIntegrationInvoice />} />

              <Route path="/campanhas" element={<CampaignPage />} />
              <Route path="/campanhas/adicionar" element={<CampaignCreatePage />} />
              <Route path="/campanhas/:id/editar" element={<CampaignEditPage />} />
              <Route path="/campanhas/:id/analisar" element={<CampaignViewPage />} />

              <Route path="/integrações" element={<IntegrationsPageAdd />} />
              <Route path="/integrações/adicionar" element={<AddIntegration />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
